/**
 * The live-layer impression effect (spec §9), extracted from its four
 * copy-pasted sites (#967 review): `LiveHomePill` / `LiveMeetversePills` pill
 * items, and the Home-recap / Events-tab recap card wrappers (via
 * `useRecapCard`).
 *
 * Fires `live_surface_displayed` with the STANDARD payload — `surface` +
 * `phase` + `buildEventContextPayload` + `buildEventParentSpacePayload` —
 * exactly once per appearance per mount:
 *
 * - Default (`appearanceKey` omitted): once per EVENT per mount. Re-renders
 *   are guard-set no-ops; a remount is a new impression (the recap-card
 *   guard). The guard keys by `event.id`, so a mounted instance whose event
 *   prop swaps (list slot reuse without a remount key) fires for the new
 *   event instead of inheriting the old one's guard (#967 review). Today's
 *   call sites all remount via keys — belt for future consumers.
 * - `appearanceKey` (the pill surfaces pass `surface:phase`): once per
 *   distinct event+key per mount, so a soon→live flip inside an id-keyed row
 *   slot — which deliberately does NOT remount — is a genuinely new artifact
 *   and fires again (the Home rail guard); `surface` rides the key too so a
 *   merged row's entry switching source (personal↔public) in that same slot
 *   counts as a new artifact just the same.
 * - `skipWhen` (the recap cards pass `hidden`): holds the impression while
 *   true; the effect fires on the render it clears. A surface that never
 *   shows never counts.
 *
 * PHASE (Dexter's review of PR #1035): every impression carries the artifact's
 * derived phase. Since the pill rows widened to `upcoming`, a surface's
 * impression volume mixes live, soon and far-future rows that convert at wildly
 * different rates — an upcoming pill is a much weaker tap than a live one — so a
 * displayed→tapped rate read per surface alone takes an unreconcilable
 * step-change on deploy. The dimension keeps the series splittable. It is a
 * REQUIRED option, not an optional one: every consumer of the shared hook
 * declares its phase, so no surface can go dark on the dimension by omission.
 * `live_surface_tapped` carries the same field at its own call sites.
 *
 * useEffect is the sanctioned tool here (house rule: analytics-on-mount IS
 * external-system synchronization). Payload/firing changes belong HERE, once
 * — the payload contract is pinned per surface by each surface's suite.
 */
import { useEffect, useRef } from 'react';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS, type LiveSurface } from '@lib/analytics-events';
import { buildEventContextPayload, buildEventParentSpacePayload } from '@lib/analytics/payloads';
import type { EventRead } from '@models/api';
import type { LiveEventPhase } from '@utils/eventLiveness';

/** Every phase an artifact can be drawn for; `none` renders nothing anywhere. */
export type LiveImpressionPhase = Exclude<LiveEventPhase, 'none'>;

export interface UseLiveImpressionOptions {
  /**
   * The phase the artifact is drawn for, stamped on the payload (see the
   * header): the rail phase for pill rows, `ended-recap` for recap cards.
   * Required — the dimension is only useful if it is never missing.
   */
  phase: LiveImpressionPhase;
  /** Hold the impression while true (e.g. a hidden recap card); fires once it clears. */
  skipWhen?: boolean;
  /**
   * Appearance identity within the mount — fires once per distinct value
   * (pill rows pass the phase). Omitted = one impression per mount. NOT
   * derived from `phase`: the guard stays the caller's, exactly as before.
   */
  appearanceKey?: string;
}

export function useLiveImpression(
  surface: LiveSurface,
  event: EventRead,
  options: UseLiveImpressionOptions,
): void {
  const { phase } = options;
  const skipWhen = options.skipWhen ?? false;
  const appearanceKey = options.appearanceKey ?? '';
  const { trackEvent } = useAmplitudeTracking();

  // Fired-appearance guard: re-renders (30s ticks, refetch identity churn,
  // hidden flips after firing) are no-ops. A ref Set, not state — firing must
  // never re-render (rerender-use-ref-transient-values). Keyed by EVENT +
  // appearance so the per-event contract holds even when a mounted instance's
  // event prop swaps (#967 review) — a same-id re-render still never refires.
  const firedKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const firedKey = `${event.id}:${appearanceKey}`;
    if (skipWhen || firedKeysRef.current.has(firedKey)) {
      return;
    }
    firedKeysRef.current.add(firedKey);
    trackEvent(ANALYTICS_EVENTS.live_surface_displayed, {
      surface,
      phase,
      ...buildEventContextPayload(event),
      ...buildEventParentSpacePayload(event),
    });
  }, [skipWhen, appearanceKey, phase, surface, event, trackEvent]);
}
